//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import ReactSelect from 'react-select'
import map from 'lodash/map'
import flatMapDepth from 'lodash/flatMapDepth'
import isArray from 'lodash/isArray'
import { useTranslation } from 'react-i18next'

import Tooltip from 'components/Shared/Tooltip/Index'
import { StyledSelectWrapper, getCustomStyles } from './utils'
import SelectControl from './components/SelectControl'
import Menu from './components/Menu'
import ClearIndicator from './components/ClearIndicator'
import DropdownIndicator from './components/DropdownIndicator'
import LoadingIndicator from './components/LoadingIndicator'
import MultiValueContainer from './components/MultiValueContainer'
import MultiValueLabel from './components/MultiValueLabel'
import MultiValueRemove from './components/MultiValueRemove'
import NoOptionsMessage from './components/NoOptionsMessage'
import Input from './components/Input'
import SingleValue from './components/SingleValue'
import Option from './components/Option'


/**
 *  Results:
 *    ['a', 'b']         => [{value: 'a', label: 'a'},  {value: 'b', label: 'b'}]
 *    {a: 'aa', b: 'bb'} => [{value: 'a', label: 'aa'}, {value: 'b', label: 'bb'}]
 *    [{value: 'a', name: 'aa'}] => [{value: 'a', label: 'aa'}]
 *    [{value: 'a', label: 'aa'}] => no changes
 */
const normalizedOptions = (optionsItems) => {
  let result = []
  if (isArray(optionsItems)) {
    if (typeof optionsItems[0] === 'object') {
      result = optionsItems.map(option => (
        { value: option.value, label: option.name || option.label || '' }
      ))
    } else {
      result = optionsItems.map(option => (
        { value: option, label: option }
      ))
    }
  } else {
    result = Object.keys(optionsItems).map(key => (
      { value: key, label: optionsItems[key] }
    ))
  }

  return result
}

// To support groups of options in this format:
//   [
//     {label: 'group 1', options: [{value: '...', label: '...', ...}]},
//     {label: 'group 2', options: [{value: '...', label: '...', ...}]},
//   ]
// Returns array of options in [{value: 'a', label: 'aa'}, ...] format or empty array
const getFlatGroupOptions = (options) => {
  return flatMapDepth(options, 'options', 1).filter(Boolean)
}

const Select = ({
  value, onChange, options, disabled, tooltipText, iconComponentsMap,
  multiselect, usePortal, portalZindex, className, isLoading,
  placeholder, width, isSearchable = true, dataTest,
}) => {
  const { t } = useTranslation()

  let optionsData, selectedOption, flatGroupOptions
  if (!multiselect) {
    // allow null as a value, convert numbers to strings
    const stringValueOrNull = value !== null ? ((value || value === 0) ? value.toString() : '') : null
    flatGroupOptions = getFlatGroupOptions(options)

    // if we have group of options we don't need normalization
    if (flatGroupOptions.length > 0) {
      optionsData = options
      selectedOption = flatGroupOptions.filter(option => option.value === stringValueOrNull)
    } else {
      optionsData = normalizedOptions(options)
      selectedOption = optionsData.filter(option => option.value === stringValueOrNull)
    }
  } else {
    // multiselect mode only supports [{value: '', label: '', ...}] and [{value: '', name: '', ...}] formats of options
    // convert {value, label} to {value, name} to prevent full normalization
    optionsData = options?.map(item => ({
      ...item, // keep other option's parameters like bgColor, options (for groups)
      value: item.value,
      label: item.name || item.label,
    })) || []

    flatGroupOptions = getFlatGroupOptions(options)
    if (!flatGroupOptions.length) {
      flatGroupOptions = optionsData
    }

    selectedOption = flatGroupOptions.filter(option => (value || []).includes(option.value))
  }

  const handleChange = (item) => {
    const newValue = multiselect ? map(item, 'value') : item.value
    const sameSelected = !multiselect && value === newValue

    if (!sameSelected) {
      onChange(newValue)
    }
  }

  const tooltipId = 'selectTooltipId'

  const components = {
    IndicatorSeparator: () => null,
    Control: SelectControl,
    Menu: Menu,
    DropdownIndicator: DropdownIndicator,
    LoadingIndicator: LoadingIndicator,
    ClearIndicator: ClearIndicator,
    MultiValueContainer: MultiValueContainer,
    MultiValueLabel: MultiValueLabel,
    MultiValueRemove: MultiValueRemove,
    NoOptionsMessage: NoOptionsMessage,
    Input: Input,
    SingleValue: SingleValue,
    Option: Option,
  }

  return (
    <StyledSelectWrapper
      width={width}
      className={className}
      disabled={disabled}
      data-tooltip-content={tooltipText}
      data-tooltip-id={tooltipId}
      data-test={dataTest}
    >
      <Tooltip place='bottom' id={tooltipId} />
      <ReactSelect
        isLoading={isLoading}
        isMulti={multiselect}
        closeMenuOnSelect={!multiselect}
        hideSelectedOptions={false}
        value={selectedOption}
        options={optionsData}
        onChange={handleChange}
        components={components}
        styles={getCustomStyles({ portalZindex })}
        menuPortalTarget={usePortal ? document.body : undefined}
        isDisabled={disabled}
        defaultMenuIsOpen={false} /* use it for debugging */
        classNamePrefix='react-select'
        noOptionsMessage={() => t('select.noOptions')}
        placeholder={placeholder || t('select.placeholder')}
        isSearchable={isSearchable}
        menuPlacement='auto'
        // custom props
        iconComponentsMap={iconComponentsMap}
      />
    </StyledSelectWrapper>
  )
}

Select.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.number.isRequired,
    PropTypes.array.isRequired,
  ]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.oneOfType([
    PropTypes.array.isRequired,
    PropTypes.object.isRequired,
  ]),
  disabled: PropTypes.bool,
  tooltipText: PropTypes.string,
  iconComponentsMap: PropTypes.object,
  multiselect: PropTypes.bool,
  usePortal: PropTypes.bool,
  portalZindex: PropTypes.number,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  width: PropTypes.string,
  isSearchable: PropTypes.bool,
  dataTest: PropTypes.string,
}

export default Select
